import {
    zodResidentialAddressLine2Validation,
    zodResidentialAddressValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useCallback } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { isEmpty } from '../../../../common/misc';
import { US_STATES } from '../../../../common/types/us-states';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const addressSchema = z.object({
    address: zodResidentialAddressValidation,
    apartment: zodResidentialAddressLine2Validation,
    city: z.string().min(1, 'Required field'),
    state: z
        .string()
        .refine(
            (value) => US_STATES.some((state) => state.value === value),
            'Invalid state',
        ),
    zipCode: z.string().length(5, 'Zip code must be exactly 5 characters'),
});

type AddressSchema = z.infer<typeof addressSchema>;

interface AddressProps {
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
}

const Address = ({ onPressNext }: AddressProps) => {
    const {
        register,
        control,
        setFocus,
        handleSubmit,
        watch,
        trigger,
        formState: { errors, isValid },
    } = useForm<AddressSchema>({
        resolver: zodResolver(addressSchema),
        defaultValues: {
            address: '',
            apartment: '',
            city: '',
            state: '',
            zipCode: '',
        },
    });

    const triggerValidationIfAllFieldsFilled = useCallback(async () => {
        const { address, city, state, zipCode } = watch();
        if (address && city && state && zipCode) {
            await trigger();
        }
    }, [watch, trigger]);

    const onSubmit: SubmitHandler<AddressSchema> = ({
        address,
        apartment,
        city,
        state,
        zipCode,
    }) => {
        onPressNext({
            line1: address,
            ...(!isEmpty(apartment) && { line2: apartment }),
            city,
            state,
            postalCode: zipCode,
            countryCode: 'US',
        });
    };

    return (
        <YStack flex={1} justifyContent="space-between">
            <View>
                <H5Emphasized marginBottom={8}>
                    What&apos;s your permanent address?
                </H5Emphasized>
                <BodyMediumRegular color="$gray900" marginBottom={24}>
                    You&apos;ll need a permanent address in the United States to
                    open an account.
                </BodyMediumRegular>

                <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                    <YStack gap={12}>
                        <ControlledInput
                            name="address"
                            placeholder="Street address"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="words"
                            autoComplete="address-line1"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('apartment');
                            }}
                            onBlur={triggerValidationIfAllFieldsFilled}
                        />
                        <ControlledInput
                            name="apartment"
                            placeholder="Apt, suite, etc. (optional)"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="words"
                            autoComplete="address-line2"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('city');
                            }}
                            onBlur={triggerValidationIfAllFieldsFilled}
                        />
                        <ControlledInput
                            name="city"
                            placeholder="City"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="words"
                            autoComplete="postal-address-locality"
                            textContentType="addressCity"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('state');
                            }}
                            onBlur={triggerValidationIfAllFieldsFilled}
                        />
                        <XStack gap={12} justifyContent="space-evenly">
                            <View flex={1}>
                                <ControlledInput
                                    name="state"
                                    placeholder="State"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    minWidth="50%"
                                    textContentType="addressState"
                                    autoCapitalize="characters"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('zipCode');
                                    }}
                                    maxLength={2}
                                    formatValueOnChange={(_, nextValue) =>
                                        nextValue.toUpperCase()
                                    }
                                    onBlur={triggerValidationIfAllFieldsFilled}
                                />
                            </View>
                            <View flex={1}>
                                <ControlledInput
                                    name="zipCode"
                                    placeholder="ZIP code"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    minWidth="50%"
                                    autoComplete="postal-code"
                                    type="numeric"
                                    inputMode="numeric"
                                    enterKeyHint="done"
                                    keyboardType="numbers-and-punctuation"
                                    onSubmitEditing={(_) => {
                                        Keyboard.dismiss();
                                    }}
                                    onBlur={triggerValidationIfAllFieldsFilled}
                                />
                            </View>
                        </XStack>
                    </YStack>
                </Form>
            </View>
            <KycButton onPress={handleSubmit(onSubmit)} disabled={!isValid} />
        </YStack>
    );
};

export default Address;
